import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, ImageIcon, Loader2, Search, DollarSign } from "lucide-react";
import { api, peso, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import BulkPriceEditor from "./BulkPriceEditor";

const EMPTY = { name: "", description: "", price: 0, category: "Groceries", subcategory: "", unit: "pc", stock: 0, image_url: "", active: true, plu: "", barcode: "", cost_price: "", margin_percent: 15 };
const FILTER_CATS = ["All", "Groceries", "Meats", "Condiments", "Frozen Products", "Sunjin Feeds"];
// Categories where the smart cost+margin price helper is hidden — meat cuts have
// volatile day-to-day pricing and Sunjin Feeds use fixed sack prices via variants.
const MANUAL_PRICING_CATS = new Set(["Meats", "Sunjin Feeds"]);

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () => api.get("/products/all").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  // Deep-link: /admin/products?edit=<id> opens that product directly. Used by
  // the Storefront Quality Audit to fix flagged items with one click.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && products.length > 0) {
      const p = products.find((x) => x.id === editId);
      if (p) {
        openEdit(p);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchParams]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return products.filter((p) => {
      if (filterCat !== "All" && p.category !== filterCat) return false;
      if (!query) return true;
      return (
        (p.name || "").toLowerCase().includes(query) ||
        (p.plu || "").toLowerCase().includes(query) ||
        (p.barcode || "").toLowerCase().includes(query) ||
        (p.subcategory || "").toLowerCase().includes(query)
      );
    });
  }, [products, q, filterCat]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/upload/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Image uploaded & watermarked");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        stock: Number(form.stock),
        subcategory: form.subcategory || null,
        plu: form.plu ? String(form.plu).trim() : null,
        barcode: form.barcode ? String(form.barcode).trim() : null,
        cost_price: form.cost_price === "" || form.cost_price == null ? null : Number(form.cost_price),
        margin_percent: form.margin_percent === "" || form.margin_percent == null ? null : Number(form.margin_percent),
      };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#1C2826]">Products</h2>
          <p className="text-sm text-[#4A5D5A]">{filtered.length} of {products.length} item{products.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkOpen(true)}
            className="h-10 rounded-lg border-[#2F5233] text-[#2F5233] hover:bg-[#EAF5EC]"
            data-testid="bulk-edit-prices-btn"
          >
            <DollarSign className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Bulk prices</span>
          </Button>
          <Button onClick={openNew} className="bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-lg" data-testid="new-product-btn">
            <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">New product</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#738380]" />
          <Input
            placeholder="Search by name, PLU or barcode..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 h-11 bg-white"
            data-testid="product-filter-search"
          />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-40 h-11 bg-white" data-testid="product-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            {FILTER_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {(q || filterCat !== "All") && (
          <Button variant="outline" onClick={() => { setQ(""); setFilterCat("All"); }} className="h-11" data-testid="product-filter-reset">
            Reset
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#E1E5DF] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#F9F9F7] text-left text-xs uppercase tracking-wider text-[#4A5D5A]">
            <tr><th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E1E5DF]">
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="h-10 w-10 rounded object-cover bg-[#F1F3EE]" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-[#F1F3EE] flex items-center justify-center">
                        <ImageIcon className="h-4 w-4 text-[#A0AAA7]" />
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-[#1C2826]">{p.name}</div>
                      <div className="text-xs text-[#738380]">
                        {p.unit}
                        {p.plu && <span className="ml-2 text-[10px] font-bold text-[#2F5233]">PLU {p.plu}</span>}
                        {p.barcode && <span className="ml-2 text-[10px] text-[#738380]">BC {p.barcode}</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3">{p.category}</td>
                <td className="p-3 font-semibold">{peso(p.price)}</td>
                <td className="p-3">
                  <span className={`font-semibold ${p.stock <= 5 ? "text-[#E74C3C]" : "text-[#1C2826]"}`}>{p.stock}</span>
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`delete-${p.id}`}><Trash2 className="h-4 w-4 text-[#E74C3C]" /></Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#738380]">{products.length === 0 ? "No products yet" : "No products match your filters"}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[92vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E1E5DF]">
            <DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 px-6 py-4 overflow-y-auto flex-1" data-testid="product-form-scroll">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Groceries">Groceries</SelectItem>
                    <SelectItem value="Meats">Meats</SelectItem>
                    <SelectItem value="Condiments">Condiments</SelectItem>
                    <SelectItem value="Frozen Products">Frozen Products</SelectItem>
                    <SelectItem value="Sunjin Feeds">Sunjin Feeds</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="product-unit" /></div>
              {MANUAL_PRICING_CATS.has(form.category) ? (
                <div>
                  <Label>Selling Price (₱)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    data-testid="product-price"
                  />
                  <p className="text-[11px] text-[#738380] mt-1">
                    {form.category === "Meats" ? "Meat prices vary daily — enter today's selling price." : "Set the base sack price; variants auto-derive."}
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Selling Price (₱)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.price}
                    readOnly
                    className="bg-[#F1F3EE] cursor-not-allowed"
                    data-testid="product-price"
                  />
                  <p className="text-[11px] text-[#2F5233] font-medium mt-1">↓ Auto-calculated from cost + margin below</p>
                </div>
              )}
            </div>

            {!MANUAL_PRICING_CATS.has(form.category) && (
              <div className="bg-[#F8F9F5] border border-[#E1E5DF] rounded-xl p-3 sm:p-4" data-testid="smart-pricing-block">
                <div className="text-xs uppercase tracking-wider font-bold text-[#2F5233] mb-2">Smart Pricing · Admin Only</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Purchase Price (₱)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="What you pay supplier"
                      value={form.cost_price ?? ""}
                      onChange={(e) => {
                        const cost = e.target.value;
                        const margin = Number(form.margin_percent ?? 15);
                        const price = cost === "" ? 0 : round2(Number(cost) * (1 + margin / 100));
                        setForm({ ...form, cost_price: cost, price });
                      }}
                      data-testid="product-cost-price"
                    />
                  </div>
                  <div>
                    <Label>Profit Margin (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={form.margin_percent ?? 15}
                      onChange={(e) => {
                        const margin = e.target.value;
                        const cost = Number(form.cost_price ?? 0);
                        const price = cost === 0 ? form.price : round2(cost * (1 + Number(margin || 0) / 100));
                        setForm({ ...form, margin_percent: margin, price });
                      }}
                      data-testid="product-margin"
                    />
                    <div className="flex gap-1 mt-1.5 flex-wrap" data-testid="margin-presets">
                      {[10, 15, 20, 25, 30].map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => {
                            const cost = Number(form.cost_price ?? 0);
                            const price = cost === 0 ? form.price : round2(cost * (1 + m / 100));
                            setForm({ ...form, margin_percent: m, price });
                          }}
                          className={`text-[10px] px-2 py-0.5 rounded-full font-bold transition ${
                            Number(form.margin_percent) === m
                              ? "bg-[#2F5233] text-white"
                              : "bg-white border border-[#E1E5DF] text-[#4A5D5A] hover:border-[#2F5233]"
                          }`}
                          data-testid={`margin-preset-${m}`}
                        >
                          {m}%
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {form.cost_price && Number(form.cost_price) > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#E1E5DF] text-xs text-[#4A5D5A]" data-testid="margin-summary">
                    Cost <span className="font-semibold text-[#1C2826]">{peso(Number(form.cost_price))}</span>
                    {" → "}Sell <span className="font-semibold text-[#2F5233]">{peso(Number(form.price))}</span>
                    {" · Profit "}<span className="font-semibold text-[#2F5233]">{peso(round2(Number(form.price) - Number(form.cost_price)))}</span>
                    <span className="text-[#738380]"> per {form.unit || "unit"}</span>
                  </div>
                )}
              </div>
            )}
            {form.category === "Meats" && (
              <div>
                <Label>Meat type</Label>
                <Select value={form.subcategory || ""} onValueChange={(v) => setForm({ ...form, subcategory: v })}>
                  <SelectTrigger data-testid="product-subcategory"><SelectValue placeholder="Select meat type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pork">Pork</SelectItem>
                    <SelectItem value="Chicken">Chicken</SelectItem>
                    <SelectItem value="Beef">Beef</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock" /></div>
              <div>
                <Label>Active</Label>
                <Select value={form.active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, active: v === "yes" })}>
                  <SelectTrigger data-testid="product-active"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Visible in shop</SelectItem>
                    <SelectItem value="no">Hidden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>PLU (scale label)</Label>
                <Input
                  value={form.plu || ""}
                  onChange={(e) => setForm({ ...form, plu: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                  placeholder="e.g. 00045"
                  data-testid="product-plu"
                />
                <p className="text-[11px] text-[#738380] mt-1">5-digit code matching your weighing scale's PLU for this item (meat, rice, produce).</p>
              </div>
              <div>
                <Label>Barcode (UPC/EAN)</Label>
                <Input
                  value={form.barcode || ""}
                  onChange={(e) => setForm({ ...form, barcode: e.target.value.replace(/\D/g, "").slice(0, 14) })}
                  placeholder="e.g. 4807770100123"
                  data-testid="product-barcode"
                />
                <p className="text-[11px] text-[#738380] mt-1">Printed barcode on the packaging (for scanned packaged goods).</p>
              </div>
            </div>

            {/* Product photo with auto-watermark */}
            <div>
              <Label>Product photo</Label>
              <div className="mt-1 flex items-start gap-3">
                <div className="h-24 w-24 rounded-lg border border-[#E1E5DF] bg-[#F1F3EE] overflow-hidden flex items-center justify-center flex-shrink-0" data-testid="product-image-preview">
                  {form.image_url ? (
                    <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-[#8A9591]" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => uploadImage(e.target.files?.[0])}
                    data-testid="product-image-file"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="product-image-upload-btn"
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" /> {form.image_url ? "Replace photo" : "Upload photo"}</>
                    )}
                  </Button>
                  <p className="text-[11px] text-[#738380] leading-snug">
                    JPG/PNG/WEBP up to 10 MB. Arbe Minimart watermark is added automatically.
                  </p>
                  <Input
                    className="text-xs"
                    placeholder="Or paste image URL"
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    data-testid="product-image-url"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#E1E5DF] bg-white">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#2F5233] hover:bg-[#1F3D24] text-white" data-testid="product-save">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {bulkOpen && (
        <BulkPriceEditor
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          products={products}
          onReload={load}
        />
      )}
    </div>
  );
}
